<template>
  <main>
    <!-- CoreMedia Top ESpot -->
    <lazy-cms-section v-if="eSpots?.[0]" :section="eSpots[0]" />

    <section class="mx-a my-16 container" style="max-width: 49.375rem">
      <p class="mb-6 text-center text-sm fw-bold">
        {{ formTitle }}
      </p>
      <base-form
        ref="formRef"
        class="mx-a space-y-5"
        :class="responseRef.code === 1 ? 'hidden' : 'f-col'"
        data-test-id="form-newsletter"
        :form
        name="newsletter"
        @submit="submit"
      >
        <vf-form-field
          v-slot="{ attrs }"
          name="email"
          :rule="[
            validateRequired($t.newsletterEmailAddress),
            validateEmail(),
          ]"
        >
          <vf-input
            v-model="form.email"
            v-bind="attrs"
            required
            type="email"
          >
            {{ $t.newsletterEmailAddress }}
          </vf-input>
        </vf-form-field>
        <vf-form-field
          v-slot="{ attrs }"
          name="firstName"
          :rule="form.firstName !== '' ? [validateName($t.validation.firstName)] : []"
        >
          <vf-input
            v-model.trim="form.firstName"
            v-bind="attrs"
            type="text"
          >
            {{ $t.firstName }}
          </vf-input>
        </vf-form-field>
        <vf-form-field
          v-slot="{ attrs }"
          name="birthDate"
          :rule="[
            validateDate(),
            validateMinDate(minBirthDate, $t.dobValidationMsg),
            validateMaxDate(maxBirthDate, replaceAll($t.dobAgeRestrictionMsg, { minimalAge })),
          ]"
        >
          <input-date
            v-model="form.birthDate"
            v-bind="attrs"
            :display-format="DATE_FORMAT_DD_MM_YYYY"
            :placeholder="DATE_FORMAT_DD_MM_YYYY"
          />
        </vf-form-field>

        <vf-form-field
          v-slot="{ attrs }"
          name="privacyPolicy"
          :rule="[validateRequired('', $t.validators.requiredCheckbox)]"
        >
          <vf-checkbox
            v-model="form.privacyPolicy"
            class="fw-bold"
            size="sm"
            v-bind="attrs"
          >
            <form-legal-disclaimer :content="disclaimer" />
          </vf-checkbox>
        </vf-form-field>

        <vf-button type="submit">
          {{ $t.newsletterSubmit }}
        </vf-button>
      </base-form>

      <div v-if="responseRef.code === 1" class="f-col center" :class="responseRef.success ? 'gap-4' : 'gap-8'">
        <vf-loader v-if="loading" style="width:6rem" />

        <template v-else>
          <span :class="responseRef.success ? 'fw-bold uppercase' : ''">
            {{ responseRef.message }}
          </span>

          <base-picture v-if="responseRef.success" class="<md:w-1/2" v-bind="successImg" />

          <vf-button class="w-full md:w-1/2" @click="backToSignup">
            {{ responseRef.success ? $t.close : ctaError }}
          </vf-button>
        </template>
      </div>
    </section>

    <!-- CoreMedia Bottom ESpot -->
    <lazy-cms-section v-if="eSpots?.[1]" page-context :section="eSpots[1]" />
  </main>
</template>

<script lang="ts" setup>
const subscriptions = useSubscriptions()
const eSpots = (await useCms().getSections.generic('otw-by-vans')).data.value?.content.sections

const { data, error } = await useApi().internal.getStaticPage('otw-by-vans')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const {
  ctaError,
  disclaimer,
  emailAlreadyMessage,
  formTitle,
  successImg,
  successMessage
} = data.value

const {
  validateDate,
  validateEmail,
  validateName,
  validateRequired,
} = useLocalizedValidators()

const loading = ref(false)
const responseRef = ref({ code: 0, message: '', success: false })
const formRef = ref()

const { minimalAge: minimalAgeConfig } = useAppConfig().components.form.signUp
const minimalAge: number = minimalAgeConfig[useCountryCode()] || minimalAgeConfig
const today = await getServerDate()
const minBirthDate = new Date(DATE_MIN)
const maxBirthDate = new Date(today.getTime())
maxBirthDate.setFullYear(today.getUTCFullYear() - minimalAge)

const initialState = {
  firstName: '',
  email: '',
  birthDate: '',
  privacyPolicy: false
}
const form = reactive({ ...initialState })

const backToSignup = () => responseRef.value = { code: 0, message: '', success: false }

async function submit() {
  loading.value = true
  responseRef.value.code = 1
  const { firstName, birthDate, email } = form

  try {
    await subscriptions.newsletter({
      optIn: true,
      firstName,
      email,
      birthDate,
      acquisitionType: 'Signup'
    })
    responseRef.value = {
      code: 1,
      message: successMessage,
      success: true
    }

    form.email = ''
    form.firstName = ''
    form.birthDate = ''
    form.privacyPolicy = false

    formRef.value?.reset()
  }
  catch {
    responseRef.value = {
      code: 1,
      message: emailAlreadyMessage,
      success: false
    }
  }
  finally {
    loading.value = false
  }
}
</script>
